"use client";

import NextImage, { type ImageProps as NextImageProps } from "next/image";
import { useEffect, useState } from "react";

const DEFAULT_FALLBACK = "/brand/tutuly-jewellery-logo.png";

function shouldBypassOptimizer(src: NextImageProps["src"] | undefined): boolean {
  return typeof src === "string" && /^(\/|data:|blob:)/.test(src);
}

function normalizeImageSrc(
  src: NextImageProps["src"] | undefined,
): NextImageProps["src"] | undefined {
  if (src === undefined || src === null) return undefined;
  if (typeof src === "string" && src.trim() === "") return undefined;
  return src;
}

export interface EnhancedImageProps extends Omit<NextImageProps, "src"> {
  src?: NextImageProps["src"];
  fallbackSrc?: string;
  blurDataURL?: string;
}

function EnhancedImage(props: EnhancedImageProps) {
  const {
    src,
    alt = "",
    quality = 70,
    placeholder,
    blurDataURL: customBlurDataURL,
    fallbackSrc,
    onError,
    ...rest
  } = props;
  const [loadFailed, setLoadFailed] = useState(false);
  const normalizedSrc = normalizeImageSrc(src);

  useEffect(() => {
    setLoadFailed(false);
  }, [normalizedSrc]);

  const baseSrc = normalizedSrc ?? fallbackSrc;
  const displaySrc = loadFailed && fallbackSrc ? fallbackSrc : baseSrc;
  const effectiveAlt = alt ?? "";
  const unoptimized = rest.unoptimized ?? shouldBypassOptimizer(displaySrc);

  const effectivePlaceholder = customBlurDataURL ? "blur" : placeholder;

  const handleError: React.ComponentProps<typeof NextImage>["onError"] = (
    event: React.SyntheticEvent<HTMLImageElement>,
  ) => {
    if (fallbackSrc && normalizedSrc) {
      setLoadFailed(true);
    }
    onError?.(event);
  };

  if (displaySrc === undefined) {
    return null;
  }

  return (
    <NextImage
      key={String(displaySrc)}
      src={displaySrc}
      alt={effectiveAlt}
      onError={handleError}
      placeholder={effectivePlaceholder}
      blurDataURL={customBlurDataURL}
      {...rest}
      unoptimized={unoptimized}
      quality={quality}
    />
  );
}

/**
 * Tutuly-specific Image with default fallback and empty/error handling.
 */
export const Image = (props: EnhancedImageProps) => (
  <EnhancedImage
    fallbackSrc={DEFAULT_FALLBACK}
    {...props}
  />
);

export type ImageProps = EnhancedImageProps;
